// functions used across components related to CRUD operations
import { getTableConfigs } from './schemaCache';
import validator from "../../node_modules/@bp_api4/schema_validation/index.js";

export const deleteMixin = {
    methods: {
        /**
         * Calls API4 endpoint to delete the given records in the table.
         * @param {string} table - table in which to delete the given ids
         * @param {Array.<integer>} ids - which ids to delete in the given table
         * @returns 
         */
        async deleteRecords(table, ids) {
          const body = JSON.stringify({
            data: [{id: ids}],
            table: table
          });

          const headers = {'Content-Type': 'application/json', 'Accept': 'application/json'};
          headers.resource = `${process.env.VUE_APP_API4_DOMAIN}/crud`;
           
          const response = await fetch(`${process.env.VUE_APP_API4_DOMAIN}/users/authorize`, {
            method: "DELETE",
            headers: headers,
            credentials: 'include',
            body: body
          });
          return response;
        }
    }
};

export const insertMixin = {
    methods: {
        /**
         * Returns user input formatted according to the cell's data type
         * @param {any} payloadItem - user input in HOT cell
         * @param {string} dataType - from cell metadata (schema_tables via schema_tables_json materialized view)
         * @returns any
         */
        formatInsertPayloadItem(payloadItem, dataType) {
        
        if (
            (dataType === "integer" && payloadItem != null) ||
            (dataType === "numeric" && payloadItem != null)
        ) {
            payloadItem = payloadItem.toString().replace(/,/g, "");
        }
        if (typeof payloadItem === "string") {
            payloadItem = payloadItem.trim(); // Value of cell after change
            payloadItem = payloadItem.length === 0 ? null : payloadItem;
        }
        if (payloadItem && payloadItem.hasOwnProperty("id")) {
            payloadItem = payloadItem["id"];
        }
        if (payloadItem && payloadItem.hasOwnProperty("name")) {
            payloadItem = payloadItem["name"];
        }
        if (dataType === "ARRAY" && payloadItem) {
            payloadItem = `{${payloadItem}}`;
        }
        // when pasting from a google sheet boolean values TRUE and FALSE are upper case, they need to be lower case or are seen as strings
        if (dataType === "boolean" && payloadItem === "FALSE") {
            payloadItem = "false";
        }
        if (dataType === "boolean" && payloadItem === "TRUE") {
            payloadItem = "true";
        }
        if (
            (payloadItem === "true" ||
            payloadItem === "false" ||
            !isNaN(payloadItem)) &&
            payloadItem != null &&
            payloadItem.length > 0 &&
            dataType !== "character varying"
        ) {
            payloadItem = JSON.parse(payloadItem);
        }
        // For the photo object: photo: { name: ###### }
        if (
            payloadItem &&
            typeof payloadItem === "object" &&
            payloadItem.name &&
            typeof payloadItem.name === "number"
        ) {
            payloadItem = payloadItem.name;
        }
        
        return payloadItem;
        },

        /**
         * Returns any validation errors for a row to insert
         * @param {{data: object[], table: string}} payload 
         * @param {integer} rowIndex 
         * @returns 
         */
        async validateInsert(payload, rowIndex) {
            const tableConfigs = await getTableConfigs([payload.table]);
            const validationSchema = { data: { api4Tables: { tables: Object.values(tableConfigs) } } };
            const validate = await validator.validatePayload(
                payload,
                validationSchema,
                "POST"
            );
            
            const errorObj = {};
            if (validate && Object.entries(validate.errors).length !== 0) {
                errorObj.row = rowIndex;
                errorObj.errors = validate.errors;
            }
            return errorObj;
        },

        /**
         * Inserts the data into the table. Check message if error. Data includes new id.
         * @async
         * @param {{data: object[], table: string}} payload 
         * @returns {Promise<{success: boolean, message: string, data: object}>}
         */
        async insertRecord(payload) {
            const headers = {
                "Content-Type": "application/json",
                Accept: "application/json",
              };
            headers.resource = `${process.env.VUE_APP_API4_DOMAIN}/crud/insertOne`;

            const response = await fetch(
                `${process.env.VUE_APP_API4_DOMAIN}/users/authorize`,
                {
                    method: "POST",
                    headers: headers,
                    credentials: "include",
                    body: JSON.stringify(payload),
                }
            );
            
            return await response.json();
        }
    }
};

export const updateMixin = {
    methods: {
        /**
         * Returns object in the proper format to send to validation/crud endpoints
         * @param {string} tableToUpdate 
         * @param {string|number} idToUpdate 
         * @param {string} columnToUpdate 
         * @param {any} newValue 
         * @returns
         */
        formatUpdatePayload(tableToUpdate, idToUpdate, columnToUpdate, newValue) {
            return {
                data: [{
                    id: Number(idToUpdate),
                    [columnToUpdate]: newValue
                }],
                table: tableToUpdate
            }
        },

        /**
            * Returns any validation errors for a row to update
            * @param {{data: object[], table: string}} payload 
            * @param {integer} rowIndex 
            * @returns 
            */
        async validateUpdate(payload, rowIndex) {
            const tableConfigs = await getTableConfigs([payload.table]);
            const validationSchema = { data: { api4Tables: { tables: Object.values(tableConfigs) } } };
            const validate = await validator.validatePayload(
                payload,
                validationSchema,
                "PATCH"
            );
            
            const errorObj = {};
            if (validate && Object.entries(validate.errors).length !== 0) {
                errorObj.row = rowIndex;
                errorObj.errors = validate.errors;
            }
            return errorObj;
        },

        /**
         * Updates a column in the table. Check message if error.
         * @async
         * @param {{data: object[], table: string}} payload 
         * @returns {Promise<{success: boolean, message: string, data: object}>}
         */
        async updateRecord(payload) {
            const headers = {
                "Content-Type": "application/json",
                Accept: "application/json",
                resource: `${process.env.VUE_APP_API4_DOMAIN}/crud`
              };

            const response = await fetch(
                `${process.env.VUE_APP_API4_DOMAIN}/users/authorize`, {
                method: "PATCH",
                headers: headers,
                credentials: 'include',
                body: JSON.stringify(payload)
            });
            
            return await response.json();
        }
    }
};
